import {
  LEAVE_STATUS_LABEL_MAP,
  LeaveDuration,
  Meridiem,
  type EmployeeLeaveRequestItem,
  type FormattedEmployeeLeaveBalances,
} from '@gokrew/models/leaves';
import {
  LeaveGrantMode,
  type LeaveCycleCard,
} from '@gokrew/models/leave-cycles';

/**
 * Returns the display label for a leave status.
 */
export function getLeaveStatusLabel(
  status?: string
): 'Pending' | 'Approved' | 'Auto Approved' | 'Rejected' | 'Cancelled' {
  if (!status) return 'Pending';
  return LEAVE_STATUS_LABEL_MAP[status] ?? 'Pending';
}

export function toMeridiem(value?: string): Meridiem | undefined {
  if (!value) return undefined;
  return value === 'AM' ? Meridiem.AM : Meridiem.PM;
}

export function toDate(value?: string | Date | null): Date | undefined {
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
}

export function detectLeaveDuration(
  req: EmployeeLeaveRequestItem
): LeaveDuration {
  // Hourly leave: has a time range instead of sessions
  if (req.startTime != null && req.endTime != null) return LeaveDuration.HOURLY;

  // Multi-day leave
  if (req.startDate !== req.endDate) return LeaveDuration.MULTI_DAY;

  // Single-day leave
  if (req.startSession === req.endSession) return LeaveDuration.HALF_DAY;

  return LeaveDuration.FULL_DAY;
}

/**
 * Whether a wallet can be picked in the leave request form. A wallet with
 * days in it always can. A daily accrual wallet can too while it is empty:
 * the employee may book what they will have earned by the leave date (the
 * booking limit is checked by the server and shown by the forecast), so a
 * new hire with 0 earned today must still see it (QA 2026-10-02, QA-03).
 */
export function isRequestableLeaveBalance(
  balance: Pick<FormattedEmployeeLeaveBalances, 'totalAllocatedDays'> & {
    leaveCycle?: Pick<LeaveCycleCard, 'grantMode'>;
    tracking?: string;
  }
): boolean {
  return (
    // A record-only absence has no balance to run out of (spec 2026-10-21).
    balance.tracking === 'record_only' ||
    balance.totalAllocatedDays > 0 ||
    balance.leaveCycle?.grantMode === LeaveGrantMode.DailyAccrual
  );
}
